/**
 * climate.js
 * ---------------------------------------------------------------------------
 * Fünfte Schwesterdatei der Reihe: identische Logik, eigener Namensraum (cl-).
 *
 * Erzeugt die Bienen und Blätter im Hintergrund und lässt sie umherziehen.
 * Nähert sich der Mauszeiger auf weniger als CONFIG.fleeRadius Pixel, stieben
 * sie auseinander und beruhigen sich danach wieder auf ihr Reisetempo.
 * Glühwürmchen und Rotoren gehören nicht dazu, die laufen rein über CSS.
 *
 * Zusätzlich verschiebt eine leichte Parallaxe Mond und Windpark.
 * Kein Klick-Verhalten, keine Abhängigkeiten, kein Build.
 *
 * Startet nicht von selbst — einmal aufrufen:
 *
 *   import { initClimate } from "./climate.js";
 *   initClimate();
 *
 * Aktiviert wird jedes Element mit der Klasse .cl-scene. Wird mindestens eine
 * gefunden, bekommt der body zusätzlich die Klasse .is-climate.
 */
export function climate() {
    "use strict";

    var CONFIG = {
        bees:        6,
        leaves:      6,
        fleeRadius:  170,
        fleeForce:   1.1,
        maxSpeed:    3.6,
        beeSpeed:    0.95,
        leafSpeed:   0.32,
        wander:      0.08,
        calmDown:    0.012,
        margin:      70,
        beeSize:     [14, 26],
        leafSize:    [14, 26]
    };

    var LEAF_COLORS = ["#6fbf5e", "#8ecf6a", "#4fae72", "#a9c94f", "#5fbc93"];

    var BEE =
        '<span class="cl-critter__body">' +
        '<i class="cl-wing cl-wing--l"></i>' +
        '<i class="cl-bee-body"></i>' +
        '<i class="cl-wing cl-wing--r"></i>' +
        '</span>';

    var LEAF = '<span class="cl-critter__body"></span>';

    function reducedMotion() {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    function rand(min, max) { return min + Math.random() * (max - min); }
    function pick(list) { return list[Math.floor(Math.random() * list.length)]; }

    function create(scene) {
        var layer = scene.querySelector(".cl-critters");
        if (!layer) return;

        var W = window.innerWidth;
        var H = window.innerHeight;
        var small = W < 700;
        var critters = [];
        var pointer = { x: -9999, y: -9999, active: false };
        var running = true;
        var last = 0;

        function spawn(kind, count) {
            var isBee = kind === "bee";
            var range = isBee ? CONFIG.beeSize : CONFIG.leafSize;

            for (var i = 0; i < count; i++) {
                var size = rand(range[0], range[1]);
                var el = document.createElement("span");
                el.className = "cl-critter cl-critter--" + kind;
                el.innerHTML = isBee ? BEE : LEAF;
                el.style.setProperty("--cl-size", size.toFixed(1) + "px");
                if (!isBee) el.style.setProperty("--cl-leaf-color", pick(LEAF_COLORS));
                el.style.opacity = isBee ? "1" : rand(0.65, 0.95).toFixed(2);
                layer.appendChild(el);

                var dir = rand(0, Math.PI * 2);
                var base = (isBee ? CONFIG.beeSpeed : CONFIG.leafSpeed) * rand(0.75, 1.3);

                critters.push({
                    el: el,
                    kind: kind,
                    w: size,
                    h: isBee ? size / 1.5 : size,
                    x: rand(0, W),
                    y: rand(0, H * 0.8),
                    vx: Math.cos(dir) * base,
                    vy: Math.sin(dir) * base,
                    base: base,
                    flee: 0,
                    face: 1,
                    fleeing: false
                });
            }
        }

        spawn("bee", small ? Math.ceil(CONFIG.bees / 2) : CONFIG.bees);
        spawn("leaf", small ? Math.ceil(CONFIG.leaves / 2) : CONFIG.leaves);

        function step(now) {
            if (!running) return;
            var dt = last ? Math.min((now - last) / 16.667, 3) : 1;
            last = now;

            for (var i = 0; i < critters.length; i++) {
                var c = critters[i];

                c.vx += rand(-1, 1) * CONFIG.wander * dt;
                c.vy += rand(-1, 1) * CONFIG.wander * dt;

                if (pointer.active) {
                    var dx = c.x - pointer.x;
                    var dy = c.y - pointer.y;
                    var d = Math.sqrt(dx * dx + dy * dy);
                    if (d < CONFIG.fleeRadius) {
                        var push = 1 - d / CONFIG.fleeRadius;
                        var inv = 1 / (d || 1);
                        c.vx += dx * inv * CONFIG.fleeForce * push * dt;
                        c.vy += dy * inv * CONFIG.fleeForce * push * dt;
                        c.flee = 1;
                    }
                }
                c.flee = Math.max(0, c.flee - CONFIG.calmDown * dt);

                if (c.x < CONFIG.margin)          c.vx += 0.07 * dt;
                else if (c.x > W - CONFIG.margin) c.vx -= 0.07 * dt;
                if (c.y < CONFIG.margin)          c.vy += 0.07 * dt;
                else if (c.y > H - CONFIG.margin) c.vy -= 0.07 * dt;

                var speed = Math.sqrt(c.vx * c.vx + c.vy * c.vy) || 0.0001;
                var target = c.base + (CONFIG.maxSpeed - c.base) * c.flee;
                var next = speed + (target - speed) * 0.05 * dt;
                c.vx = c.vx / speed * next;
                c.vy = c.vy / speed * next;

                c.x += c.vx * dt;
                c.y += c.vy * dt;
                if (c.x < -c.w) c.x = W + c.w;
                else if (c.x > W + c.w) c.x = -c.w;
                if (c.y < -c.h) c.y = H + c.h;
                else if (c.y > H + c.h) c.y = -c.h;

                c.el.style.transform =
                    "translate3d(" + (c.x - c.w / 2).toFixed(1) + "px," +
                    (c.y - c.h / 2).toFixed(1) + "px,0)";

                if (c.kind === "bee") {
                    var face = c.vx < 0 ? -1 : 1;
                    if (face !== c.face) {
                        c.face = face;
                        c.el.style.setProperty("--cl-face", face);
                    }
                }

                var fleeing = c.flee > 0.2;
                if (fleeing !== c.fleeing) {
                    c.fleeing = fleeing;
                    c.el.classList.toggle("is-fleeing", fleeing);
                }
            }

            requestAnimationFrame(step);
        }

        window.addEventListener("pointermove", function (e) {
            pointer.x = e.clientX;
            pointer.y = e.clientY;
            pointer.active = true;
        }, { passive: true });

        document.addEventListener("pointerleave", function () {
            pointer.active = false;
        });

        window.addEventListener("resize", function () {
            W = window.innerWidth;
            H = window.innerHeight;
        }, { passive: true });

        document.addEventListener("visibilitychange", function () {
            if (document.hidden) {
                running = false;
            } else if (!running) {
                running = true;
                last = 0;
                requestAnimationFrame(step);
            }
        });

        requestAnimationFrame(step);
    }

    function init(scene) {
        if (scene.dataset.clReady) return;
        scene.dataset.clReady = "1";

        window.addEventListener("pointermove", function (e) {
            scene.style.setProperty("--cl-px", (e.clientX / window.innerWidth * 2 - 1).toFixed(3));
            scene.style.setProperty("--cl-py", (e.clientY / window.innerHeight * 2 - 1).toFixed(3));
        }, { passive: true });

        if (!reducedMotion()) create(scene);
    }

    function boot() {
        var scenes = document.querySelectorAll(".cl-scene");
        if (!scenes.length) return;

        document.body.classList.add("is-climate");

        Array.prototype.forEach.call(scenes, init);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", boot);
    } else {
        boot();
    }
}